import { useState } from 'react';
import { Alert, Flex, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { TransMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useFileUpload } from '@dealstack/frontend/shared/components/Documents/hooks/useFileUpload';
import { useReplaceDocumentFile } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useReplaceDocumentFile';
import { formatFileSize } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ReplaceFileModal.utils';
import {
	ACCEPT_FILES_TYPES,
	DropzoneV2,
} from '@dealstack/frontend/shared/components/DropzoneV2/DropzoneV2';

const REASON_MAX_LENGTH = 300;

interface Props {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	currentFileName: string | null | undefined;
	isReadOnly: boolean;
	opened: boolean;
	onClose: () => void;
}

export const ReplaceFileModal = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	currentFileName,
	isReadOnly,
	opened,
	onClose,
}: Props) => {
	const [reason, setReason] = useState('');
	const { uploadedFile, setUploadedFile, removeFile, handleFileUpload, isUploading } =
		useFileUpload();
	const { mutateAsync: replaceFile, isPending: isReplacing } = useReplaceDocumentFile({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
		onSuccess: onClose,
	});

	const isBusy = isUploading || isReplacing;
	const canSubmit = !!uploadedFile && reason.trim().length > 0 && !isBusy;

	const handleConfirm = async () => {
		if (!canSubmit) {
			return;
		}
		const newFileId = await handleFileUpload();
		if (!newFileId) {
			showToastV2({ state: 'error', title: 'Failed to upload replacement file' });
			return;
		}
		await replaceFile({ newFileId, comment: reason.trim() });
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			closeOnClickOutside={false}
			size="lg"
			padding="md"
			radius="md"
			withCloseButton
			centered
			title={
				<Text size="18px" fw={600}>
					Replace file
				</Text>
			}
			styles={{
				body: { backgroundColor: '#fff' },
				header: { backgroundColor: '#fff', marginBottom: 0 },
				content: { backgroundColor: '#fff' },
			}}
		>
			<Stack gap="sm">
				<Text size="14px" c="gray.7">
					{isReadOnly ? (
						<TransMipManage
							i18nKey="documents_page.replace_file.modal.description_review"
							components={{ strong: <strong /> }}
						/>
					) : (
						<TransMipManage
							i18nKey="documents_page.replace_file.modal.description_signing"
							components={{ strong: <strong /> }}
						/>
					)}
				</Text>

				<Stack gap={6}>
					<Text size="11px" fw={600} c="gray.7">
						CURRENT FILE
					</Text>
					<Flex
						align="center"
						gap="xs"
						p="sm"
						sx={(theme) => ({
							border: `1px solid ${theme.colors.gray[3]}`,
							borderRadius: theme.radius.sm,
							backgroundColor: theme.colors.gray[0],
						})}
					>
						<Icon variant="document-v3-rounded" size="md" color="#868E96" />
						<Text size="14px" c="gray.6">
							{currentFileName ?? '—'}
						</Text>
					</Flex>
				</Stack>

				<Stack gap={6}>
					<Text size="14px" fw={600}>
						Replacement file
					</Text>

					{uploadedFile ? (
						<Flex
							align="center"
							gap="xs"
							p="sm"
							sx={(theme) => ({
								border: `1px solid ${theme.colors.gray[3]}`,
								borderRadius: theme.radius.sm,
							})}
						>
							<Icon variant="document-v3-rounded" size="md" />
							<Stack gap={0} sx={{ flex: 1 }}>
								<Text size="14px">{uploadedFile.name}</Text>
								<Text size="11px" c="gray.7">
									{formatFileSize(uploadedFile.size)} · Ready to upload
								</Text>
							</Stack>
							<ButtonV2
								size="s"
								buttonTheme="light"
								variant="tertiary"
								onClick={removeFile}
								disabled={isBusy}
							>
								Remove
							</ButtonV2>
						</Flex>
					) : (
						<DropzoneV2
							onDropFile={setUploadedFile}
							accept={ACCEPT_FILES_TYPES.pdf}
							multiple={false}
							disabled={isBusy}
						/>
					)}
				</Stack>

				<Stack gap={6}>
					<Flex justify="space-between" align="center">
						<Text size="14px" fw={600}>
							Reason for replacement
						</Text>
						<Text size="11px" c="gray.7">
							{reason.length}/{REASON_MAX_LENGTH}
						</Text>
					</Flex>
					<Textarea
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						placeholder="e.g. Handled off platform."
						maxLength={REASON_MAX_LENGTH}
						minRows={3}
						autosize
					/>
					<Text size="11px" c="gray.7">
						Recorded in the audit log. Visible to support and programme managers.
					</Text>
				</Stack>

				<Alert
					color="blue"
					variant="light"
					radius="sm"
					icon={<Icon variant="information" size="lg" />}
				>
					<Text size="13px" c="gray.8">
						The new file becomes the active version immediately. The prior version stays
						accessible from the activity log.
					</Text>
				</Alert>

				<Group justify="right" gap="xs">
					<ButtonV2
						size="s"
						buttonTheme="light"
						variant="tertiary"
						onClick={onClose}
						disabled={isBusy}
					>
						Cancel
					</ButtonV2>
					<ButtonV2
						size="s"
						buttonTheme="light"
						variant="primary"
						disabled={!canSubmit}
						onClick={handleConfirm}
					>
						Replace file
					</ButtonV2>
				</Group>
			</Stack>
		</Modal>
	);
};
